import Router from 'next/router';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useTrackAddedComment } from '@air/analytics';
import { type PublicClip } from '@air/api';
import { type Clip } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';
import { useShortIdContext } from '@air/provider-short-id';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type ClipDiscussion } from '~/components/Annotations/shared/types';
import { getAnnotationAnalyticsType, hasAnnotation } from '~/components/AnnotationsV2/shared/annotationUtils';
import { useDiscussionPanelContextSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { discussionsVersionFilterSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextUtils';
import { useOnAnnotatedCommentClick } from '~/components/AssetModal/Visualizer/VideoVisualizer/utils';
import { usePDFCommentClick } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/usePDFCommentClick';
import { usePDFDiscussionPageSync } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/usePDFDiscussionPageSync';
import { DiscussionsList, type DiscussionsListProps } from '~/components/DiscussionsV2/DiscussionsList';
import { DiscussionPermissionsProvider } from '~/components/DiscussionsV2/providers/DiscussionPermissionsProvider';
import { EmptyComments } from '~/components/PublicAssetModal/components/EmptyComments';
import { PublicCommentAnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/PublicCommentAnonymousSignUpModal';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { type SSOCallbackData } from '~/components/PublicSSOCallback/utils';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { QueryParamNames } from '~/constants/search';
import { useCanViewAllVersionsOnPublicShareLink } from '~/hooks/useCanViewAllVersionsOnPublicShareLink';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useAccountContext } from '~/providers/AccountProvider';
import { centralizedBoardAncestorsSelector } from '~/store/centralizedBoard/selectors';
import {
  centralizedClipAssetIdSelector,
  centralizedClipIdSelector,
  centralizedClipIsPDFTypeSelector,
  centralizedClipTypeSelector,
  centralizedClipVersionNumberSelector,
} from '~/store/centralizedClip/selectors';
import { getCommentTime } from '~/swr-hooks/discussions/getCommentTime';
import { type CreateCommentParams, useCreateComment } from '~/swr-hooks/discussions/useCreateComment';
import { useGetDiscussionMentionsMembers } from '~/swr-hooks/discussions/useGetDiscussionMentionsMembers';
import { usePublicDeleteCommentClick } from '~/swr-hooks/discussions/usePublicDeleteCommentClick';
import { usePublicDiscussions } from '~/swr-hooks/discussions/usePublicDiscussions';
import { useSaveEditedCommentClick } from '~/swr-hooks/discussions/useSaveCommentEditClick';
import { usePublicVersionsList } from '~/swr-hooks/versions/usePublicVersionsList';
import { useSwitchPublicVersion } from '~/swr-hooks/versions/useSwitchPublicVersion';
import { setQueryParams } from '~/utils/PathUtils';
import { canEditAssetDiscussions, canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';
import { useAirSelector, useAirStore } from '~/utils/ReduxUtils';

export const PublicDiscussionsList = memo(() => {
  const store = useAirStore();
  const assetId = useSelector(centralizedClipAssetIdSelector);
  const { workspaceId: clipWorkspaceId } = usePublicWorkspace();
  const { permissions } = usePublicPermissions();
  const { canViewAllVersions: canViewAssetVersions } = useCanViewAllVersionsOnPublicShareLink();
  const canViewDiscussions = canViewAssetDiscussions(permissions);
  const { shortId } = useShortIdContext();
  const boardId = useURLBoardIdSelector();
  const clipId = useSelector(centralizedClipIdSelector);
  const { data: account } = useAccountContext();
  const ancestors = useAirSelector(centralizedBoardAncestorsSelector);
  const { mentionMembers, isLoadingMentions } = useGetDiscussionMentionsMembers({
    boardId,
    ancestors,
    workspaceId: clipWorkspaceId,
  });
  const [showSignUpModal] = useAirModal(PublicCommentAnonymousSignUpModal);
  const versionNumber = useSelector(centralizedClipVersionNumberSelector);
  const discussionsVersionFilter = useDiscussionPanelContextSelector(discussionsVersionFilterSelector);
  const { switchVersion } = useSwitchPublicVersion();
  const { createComment } = useCreateComment();
  const { trackAddedComment } = useTrackAddedComment();
  const { handleCommentDeleteClick } = usePublicDeleteCommentClick({ trackLocation: 'public-board' });

  const {
    data: discussions,
    isLoading: isInitialLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    total,
  } = usePublicDiscussions({
    boardId,
    clipId: discussionsVersionFilter === 'CURRENT' || !canViewAssetVersions ? clipId : undefined,
    assetId,
    shortId,
    isEnabled: canViewDiscussions,
  });

  usePDFDiscussionPageSync(discussions);

  const { onAnnotatedCommentClick } = useOnAnnotatedCommentClick();
  const { onPDFCommentClick } = usePDFCommentClick();
  const isPDF = useSelector(centralizedClipIsPDFTypeSelector);

  const { data: versions } = usePublicVersionsList();
  const { handleSaveEditedCommentClick } = useSaveEditedCommentClick({ trackLocation: 'public-board' });
  const canEditDiscussions = canEditAssetDiscussions(permissions);

  const onSwitchVersion = useCallback(
    (clipId: PublicClip['id']) => {
      const clip = versions?.data.clips.find((c) => c.id === clipId);
      clip && switchVersion({ version: clip, location: 'discussion' });
    },
    [switchVersion, versions?.data.clips],
  );

  const filteredDiscussions = useMemo(() => {
    switch (discussionsVersionFilter) {
      case 'ALL':
        return discussions;
      case 'CURRENT':
        return discussions.filter((d) => d.assetVersion === versionNumber);
    }
    return [];
  }, [discussions, discussionsVersionFilter, versionNumber]);

  const handleAnnotatedCommentClick: DiscussionsListProps['onAnnotatedCommentClick'] = useCallback(
    ({ clipId: commentClipId, discussionId, timestamp, hasAnnotation, pageNumber }) => {
      const clip = versions?.data.clips.find((c) => c.id === commentClipId) ?? { id: commentClipId };

      if (isPDF) {
        onPDFCommentClick({
          onSwitchVersion: (query) => {
            const currentClipId = centralizedClipIdSelector(store.getState());

            if (commentClipId !== currentClipId) {
              switchVersion({ version: clip, location: 'discussion', query });
            }
          },
          discussionId,
          hasAnnotation,
          pageNumber,
        }).catch((error) => {
          reportErrorToBugsnag({ error, context: 'Failed to open PDF comment' });
        });
      } else {
        onAnnotatedCommentClick({
          onSwitchVersion: () => onSwitchVersion(commentClipId),
          discussionId,
          timestamp,
          hasAnnotation,
        }).catch((error) => {
          reportErrorToBugsnag({ error, context: 'Failed to open annotated comment' });
        });
      }
    },
    [
      isPDF,
      onAnnotatedCommentClick,
      onPDFCommentClick,
      onSwitchVersion,
      switchVersion,
      versions?.data.clips,
      store.getState,
    ],
  );

  const handleCommentSubmit = useCallback(
    (newComment: string, existingDiscussion: ClipDiscussion, clipId: Clip['id']) => {
      Router.ready(() => {
        const commentSubmitParams: CreateCommentParams = {
          newComment,
          existingDiscussion,
          boardId,
          shortId,
        };

        trackAddedComment({
          boardId,
          clipId,
          comment: newComment,
          comment_time: getCommentTime(existingDiscussion),
          location: 'public-board',
          public: true,
          startedDiscussion: false,
          has_timestamp: !!existingDiscussion.timestamp,
          has_annotation: hasAnnotation(existingDiscussion),
          annotationType: getAnnotationAnalyticsType(existingDiscussion.annotations),
          assetType: centralizedClipTypeSelector(store.getState()),
        });

        if (account) {
          return createComment(commentSubmitParams);
        } else {
          const ssoCallbackData: SSOCallbackData = {
            url: Router.asPath,
            action: {
              action: 'reply-to-public-comment',
              data: {
                newComment,
                existingDiscussion,
                boardId,
                shortId,
              },
            },
          };

          showSignUpModal({
            onLoginSuccess: () => createComment(commentSubmitParams),
            ssoCallbackData,
          });
        }
      });
    },
    [boardId, shortId, trackAddedComment, store, account, createComment, showSignUpModal],
  );

  const handleSwitchVersion = useCallback(
    (clipId: Clip['id']) => {
      onSwitchVersion(clipId);
      setQueryParams({ [QueryParamNames.timestamp]: null, [QueryParamNames.discussionId]: null }, 'replace');
    },
    [onSwitchVersion],
  );

  return (
    <DiscussionPermissionsProvider permissions={permissions}>
      <DiscussionsList
        totalDiscussions={total}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        discussions={filteredDiscussions}
        isLoading={isInitialLoading || isFetchingNextPage}
        isLoadingMentions={isLoadingMentions}
        mentionsList={mentionMembers}
        onToggleCommentResolve={undefined}
        onAnnotatedCommentClick={handleAnnotatedCommentClick}
        onCommentDelete={canEditDiscussions ? handleCommentDeleteClick : undefined}
        onCommentEdit={canEditDiscussions ? handleSaveEditedCommentClick : undefined}
        onCommentSubmit={canEditDiscussions ? handleCommentSubmit : undefined}
        canReply={canEditDiscussions}
        onSwitchVersion={canViewAssetVersions ? handleSwitchVersion : undefined}
        shouldShowMentionsButton={true}
        emptyState={<EmptyComments />}
      />
    </DiscussionPermissionsProvider>
  );
});

PublicDiscussionsList.displayName = 'PublicDiscussionsList';
